import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { listBookings, listConsultantRequests, listEnquiries, adminLogin, getTeamToken, setTeamToken, listEmergency, listHospitalsManage, createHospital, updateHospital, deleteHospital, uploadHospitalImage, publishHospital, listAssociates, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, MapPin, RefreshCw, Phone, Mail, LogOut, Lock, Siren, Trash2, Pencil, ImagePlus, Plus } from "lucide-react";
import logo from "../assets/logo.png";

const imgSrc = (url) => (url?.startsWith("/api/") ? fileUrl(url) : url);

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function TeamInbox() {
  const [authed, setAuthed] = useState(!!getTeamToken());
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loggingIn, setLoggingIn] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [emergencies, setEmergencies] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [associates, setAssociates] = useState([]);
  const [hForm, setHForm] = useState({ name: "", area: "", ailments: "", emergency_24x7: true });
  const [editingId, setEditingId] = useState(null);
  const [hSaving, setHSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const login = async () => {
    if (!creds.email || !creds.password) {
      toast.error("Enter email and password.");
      return;
    }
    setLoggingIn(true);
    try {
      const { token } = await adminLogin(creds.email, creds.password);
      setTeamToken(token);
      setAuthed(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    setTeamToken(null);
    setAuthed(false);
  };

  const saveHospital = async () => {
    if (!hForm.name.trim()) {
      toast.error("Hospital name is required.");
      return;
    }
    const payload = {
      name: hForm.name,
      area: hForm.area,
      emergency_24x7: hForm.emergency_24x7,
      ailments: hForm.ailments.split(",").map((s) => s.trim()).filter(Boolean),
    };
    setHSaving(true);
    try {
      if (editingId) {
        await updateHospital(editingId, payload);
        toast.success("Hospital updated.");
      } else {
        await createHospital(payload);
        toast.success("Hospital added.");
      }
      setHForm({ name: "", area: "", ailments: "", emergency_24x7: true });
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setHSaving(false);
    }
  };

  const editHospital = (h) => {
    setEditingId(h.id);
    setHForm({ name: h.name, area: h.area, ailments: (h.ailments || []).join(", "), emergency_24x7: h.emergency_24x7 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeHospital = async (id) => {
    if (!window.confirm("Delete this hospital?")) return;
    try {
      await deleteHospital(id);
      load();
    } catch (e) {
      toast.error("Could not delete.");
    }
  };

  const onHospitalImage = async (id, files) => {
    if (!files?.length) return;
    try {
      await uploadHospitalImage(id, files[0]);
      toast.success("Image updated.");
      load();
    } catch (e) {
      toast.error("Image upload failed.");
    }
  };

  const togglePublish = async (h) => {
    try {
      await publishHospital(h.id, !h.published);
      toast.success(h.published ? "Hidden from website." : "Approved — now live on website.");
      load();
    } catch (e) {
      toast.error("Could not update.");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [b, c, e, em, hos, asc] = await Promise.all([
        listBookings(),
        listConsultantRequests(),
        listEnquiries(),
        listEmergency(),
        listHospitalsManage(),
        listAssociates(),
      ]);
      setBookings(b);
      setCallbacks(c);
      setEnquiries(e);
      setEmergencies(em);
      setHospitals(hos);
      setAssociates(asc);
    } catch (err) {
      if (err?.response?.status === 401) {
        setTeamToken(null);
        setAuthed(false);
        toast.error("Session expired. Please log in again.");
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!authed) return;
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [authed]);

  const th = "text-left text-xs font-semibold uppercase tracking-wide text-brand-ink/50 px-3 py-2 whitespace-nowrap";
  const td = "px-3 py-3 text-sm text-brand-ink align-top border-t border-border";

  if (!authed) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center px-6">
        <div className="w-full max-w-sm rounded-3xl bg-white border border-border p-8" data-testid="team-login">
          <div className="flex items-center gap-3 mb-6">
            <img src={logo} alt="ElderCare24" className="h-10 w-auto" />
            <div>
              <p className="font-serif text-2xl leading-none text-brand-ink">
                Eldercare<span className="text-brand">24</span>
              </p>
              <p className="text-xs text-brand-ink/50 mt-1">Team login</p>
            </div>
          </div>
          <p className="text-sm text-brand-ink/60 mb-5 flex items-center gap-1.5">
            <Lock size={14} className="text-brand" /> This inbox holds private client details.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="text-brand-ink/80 text-sm mb-1.5 block">Email</Label>
              <Input
                data-testid="team-login-email"
                type="email"
                value={creds.email}
                onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))}
                placeholder="admin@eldercare24.com"
              />
            </div>
            <div>
              <Label className="text-brand-ink/80 text-sm mb-1.5 block">Password</Label>
              <Input
                data-testid="team-login-password"
                type="password"
                value={creds.password}
                onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && login()}
                placeholder="••••••••"
              />
            </div>
            <Button
              data-testid="team-login-btn"
              onClick={login}
              disabled={loggingIn}
              className="w-full rounded-full bg-brand hover:bg-brand-hover text-white h-11"
            >
              {loggingIn ? "Signing in..." : "Sign in"}
            </Button>
            <Link to="/" className="block text-center text-sm text-brand-ink/50 hover:text-brand mt-2">
              Back to site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream">
      <header className="bg-brand-ink text-brand-cream">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="ElderCare24" className="h-9 w-auto" />
            <div>
              <p className="font-serif text-2xl leading-none">
                Eldercare<span className="text-brand-gold">24</span>
              </p>
              <p className="text-xs text-brand-cream/60 mt-1">Team inbox · submitted client details</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              data-testid="refresh-btn"
              onClick={load}
              variant="ghost"
              size="sm"
              className="text-brand-cream/80 hover:text-white"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
            </Button>
            <Link to="/" className="text-sm text-brand-cream/70 hover:text-brand-gold flex items-center gap-1">
              <ArrowLeft size={16} /> Site
            </Link>
            <button
              data-testid="team-logout-btn"
              onClick={logout}
              className="text-sm text-brand-cream/70 hover:text-brand-gold flex items-center gap-1"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="bookings">
          <TabsList className="bg-brand-sand">
            <TabsTrigger value="bookings" data-testid="tab-bookings">
              Visit requests ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="callbacks" data-testid="tab-callbacks">
              Callbacks ({callbacks.length})
            </TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">
              Enquiries ({enquiries.length})
            </TabsTrigger>
            <TabsTrigger value="emergency" data-testid="tab-emergency">
              Emergency ({emergencies.length})
            </TabsTrigger>
            <TabsTrigger value="hospitals" data-testid="tab-hospitals">
              Hospitals ({hospitals.length})
            </TabsTrigger>
            <TabsTrigger value="associates" data-testid="tab-associates">
              Associates ({associates.length})
            </TabsTrigger>
          </TabsList>

          {/* Bookings */}
          <TabsContent value="bookings">
            <div className="rounded-2xl bg-white border border-border overflow-x-auto mt-4">
              <table className="w-full" data-testid="bookings-table">
                <thead>
                  <tr>
                    <th className={th}>Parent</th>
                    <th className={th}>Address</th>
                    <th className={th}>Live location</th>
                    <th className={th}>Guardian</th>
                    <th className={th}>Phone</th>
                    <th className={th}>Priority</th>
                    <th className={th}>Doctor domain</th>
                    <th className={th}>Services</th>
                    <th className={th}>Schedule</th>
                    <th className={th}>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} data-testid={`booking-row-${b.id}`}>
                      <td className={td}>
                        <div className="font-medium">{b.parent_name}</div>
                        <div className="text-xs text-brand-ink/50">{b.area}</div>
                      </td>
                      <td className={`${td} max-w-[180px]`}>{b.parent_address}</td>
                      <td className={td}>
                        {b.parent_location ? (
                          <a
                            href={b.parent_location}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-brand font-medium"
                          >
                            <MapPin size={14} /> Map
                          </a>
                        ) : (
                          <span className="text-brand-ink/30">—</span>
                        )}
                      </td>
                      <td className={td}>{b.child_name}</td>
                      <td className={td}>{b.child_phone}</td>
                      <td className={td}>
                        {b.priority ? (
                          <span className="inline-flex items-center rounded-full bg-brand text-white text-xs font-semibold px-2.5 py-1">Priority</span>
                        ) : (
                          <span className="text-brand-ink/30">—</span>
                        )}
                      </td>
                      <td className={td}>{b.expertise_domain || "—"}</td>
                      <td className={`${td} max-w-[180px]`}>{b.services.join(", ")}</td>
                      <td className={td}>
                        <div>{b.preferred_date} · {b.time_slot}</div>
                        <div className="text-xs text-brand-ink/50">
                          {b.frequency}
                          {b.schedule_days?.length ? ` · ${b.schedule_days.join(", ")}` : ""}
                        </div>
                      </td>
                      <td className={`${td} text-xs text-brand-ink/50 whitespace-nowrap`}>{fmt(b.created_at)}</td>
                    </tr>
                  ))}
                  {bookings.length === 0 && (
                    <tr><td className={`${td} text-brand-ink/40`} colSpan={10}>No visit requests yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Callbacks */}
          <TabsContent value="callbacks">
            <div className="rounded-2xl bg-white border border-border overflow-x-auto mt-4">
              <table className="w-full" data-testid="callbacks-table">
                <thead>
                  <tr>
                    <th className={th}>Name</th>
                    <th className={th}>Phone</th>
                    <th className={th}>Note</th>
                    <th className={th}>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {callbacks.map((c) => (
                    <tr key={c.id} data-testid={`callback-row-${c.id}`}>
                      <td className={td}>{c.name}</td>
                      <td className={td}>
                        <span className="inline-flex items-center gap-1"><Phone size={13} className="text-brand" /> {c.phone}</span>
                      </td>
                      <td className={`${td} max-w-[320px]`}>{c.note || "—"}</td>
                      <td className={`${td} text-xs text-brand-ink/50 whitespace-nowrap`}>{fmt(c.created_at)}</td>
                    </tr>
                  ))}
                  {callbacks.length === 0 && (
                    <tr><td className={`${td} text-brand-ink/40`} colSpan={4}>No callback requests yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Enquiries */}
          <TabsContent value="enquiries">
            <div className="rounded-2xl bg-white border border-border overflow-x-auto mt-4">
              <table className="w-full" data-testid="enquiries-table">
                <thead>
                  <tr>
                    <th className={th}>Name</th>
                    <th className={th}>Email</th>
                    <th className={th}>Phone</th>
                    <th className={th}>Message</th>
                    <th className={th}>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {enquiries.map((e) => (
                    <tr key={e.id} data-testid={`enquiry-row-${e.id}`}>
                      <td className={td}>{e.name}</td>
                      <td className={td}>
                        <span className="inline-flex items-center gap-1"><Mail size={13} className="text-brand" /> {e.email}</span>
                      </td>
                      <td className={td}>{e.phone || "—"}</td>
                      <td className={`${td} max-w-[360px]`}>{e.message}</td>
                      <td className={`${td} text-xs text-brand-ink/50 whitespace-nowrap`}>{fmt(e.created_at)}</td>
                    </tr>
                  ))}
                  {enquiries.length === 0 && (
                    <tr><td className={`${td} text-brand-ink/40`} colSpan={5}>No enquiries yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Emergency */}
          <TabsContent value="emergency">
            <div className="rounded-2xl bg-white border border-border overflow-x-auto mt-4">
              <table className="w-full" data-testid="emergency-table">
                <thead>
                  <tr>
                    <th className={th}>Name</th>
                    <th className={th}>Phone</th>
                    <th className={th}>Hospital</th>
                    <th className={th}>Ailment</th>
                    <th className={th}>Message</th>
                    <th className={th}>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {emergencies.map((e) => (
                    <tr key={e.id} data-testid={`emergency-row-${e.id}`} className="bg-red-50/40">
                      <td className={td}>{e.name}</td>
                      <td className={td}>
                        <span className="inline-flex items-center gap-1"><Phone size={13} className="text-red-500" /> {e.phone}</span>
                      </td>
                      <td className={td}>{e.hospital || "—"}</td>
                      <td className={td}>{e.ailment || "—"}</td>
                      <td className={`${td} max-w-[280px]`}>{e.message || "—"}</td>
                      <td className={`${td} text-xs text-brand-ink/50 whitespace-nowrap`}>{fmt(e.created_at)}</td>
                    </tr>
                  ))}
                  {emergencies.length === 0 && (
                    <tr><td className={`${td} text-brand-ink/40`} colSpan={6}>No emergency requests yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Hospitals management */}
          <TabsContent value="hospitals">
            <div className="grid lg:grid-cols-3 gap-6 mt-4">
              <div className="lg:col-span-1">
                <div className="rounded-2xl bg-white border border-border p-6" data-testid="hospital-form">
                  <h3 className="font-serif text-xl text-brand-ink mb-4">
                    {editingId ? "Edit hospital" : "Add a hospital"}
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-brand-ink/80 text-sm mb-1.5 block">Name</Label>
                      <Input data-testid="hospital-name" value={hForm.name} onChange={(e) => setHForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Fortis Hospital" />
                    </div>
                    <div>
                      <Label className="text-brand-ink/80 text-sm mb-1.5 block">Area / locality</Label>
                      <Input data-testid="hospital-area" value={hForm.area} onChange={(e) => setHForm((f) => ({ ...f, area: e.target.value }))} placeholder="e.g. Malviya Nagar, Jaipur" />
                    </div>
                    <div>
                      <Label className="text-brand-ink/80 text-sm mb-1.5 block">Ailments / specialties (comma separated)</Label>
                      <Input data-testid="hospital-ailments" value={hForm.ailments} onChange={(e) => setHForm((f) => ({ ...f, ailments: e.target.value }))} placeholder="Cardiac, Ortho, Neuro" />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-brand-ink cursor-pointer">
                      <input type="checkbox" data-testid="hospital-emergency" checked={hForm.emergency_24x7} onChange={(e) => setHForm((f) => ({ ...f, emergency_24x7: e.target.checked }))} className="h-4 w-4 accent-brand" />
                      24×7 emergency available
                    </label>
                    <div className="flex gap-2 pt-1">
                      <Button data-testid="hospital-save" onClick={saveHospital} disabled={hSaving} className="rounded-full bg-brand hover:bg-brand-hover text-white px-5">
                        {hSaving ? "Saving..." : editingId ? "Update" : <><Plus size={15} /> Add</>}
                      </Button>
                      {editingId && (
                        <Button variant="ghost" onClick={() => { setEditingId(null); setHForm({ name: "", area: "", ailments: "", emergency_24x7: true }); }} className="rounded-full">
                          Cancel
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-brand-ink/50">Add a hospital, then use "Photo" on its card to upload a picture.</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4" data-testid="hospitals-manage-list">
                {hospitals.map((h) => (
                  <div key={h.id} className="rounded-2xl bg-white border border-border overflow-hidden" data-testid={`hospital-manage-${h.id}`}>
                    <div className="aspect-[16/9] bg-brand-sand overflow-hidden">
                      {h.image_url ? (
                        <img src={imgSrc(h.image_url)} alt={h.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-brand-ink/30 text-sm">No photo</div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium text-brand-ink truncate">{h.name}</p>
                          <p className="text-xs text-brand-ink/50 truncate">{h.area}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${h.published ? "bg-brand-sage/20 text-brand-sage" : "bg-brand-sand text-brand-ink/50"}`} data-testid={`hospital-status-${h.id}`}>
                            {h.published ? "Live" : "Draft"}
                          </span>
                          {h.emergency_24x7 && <span className="text-xs text-red-600 font-semibold">24×7</span>}
                        </div>
                      </div>
                      <div className="mt-3">
                        <button
                          onClick={() => togglePublish(h)}
                          data-testid={`hospital-publish-${h.id}`}
                          className={`w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold rounded-full px-3 py-2 ${h.published ? "bg-brand-sand text-brand-ink hover:bg-brand-sand/70" : "bg-brand text-white hover:bg-brand-hover"}`}
                        >
                          {h.published ? "Unpublish (hide)" : "Approve & publish"}
                        </button>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(h.ailments || []).map((a) => (
                          <span key={a} className="text-xs bg-brand-sand rounded-full px-2 py-0.5 text-brand-ink/70">{a}</span>
                        ))}
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <label className="inline-flex items-center gap-1 text-xs font-semibold text-brand cursor-pointer border border-brand/40 rounded-full px-3 py-1.5 hover:bg-brand/5" data-testid={`hospital-photo-${h.id}`}>
                          <ImagePlus size={13} /> Photo
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => onHospitalImage(h.id, e.target.files)} />
                        </label>
                        <button onClick={() => editHospital(h)} data-testid={`hospital-edit-${h.id}`} className="inline-flex items-center gap-1 text-xs text-brand-ink/70 border border-border rounded-full px-3 py-1.5 hover:border-brand/40">
                          <Pencil size={13} /> Edit
                        </button>
                        <button onClick={() => removeHospital(h.id)} data-testid={`hospital-delete-${h.id}`} className="inline-flex items-center gap-1 text-xs text-red-600 border border-red-200 rounded-full px-3 py-1.5 hover:bg-red-50">
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {hospitals.length === 0 && <p className="text-brand-ink/40 text-sm">No hospitals yet. Add one on the left.</p>}
              </div>
            </div>
          </TabsContent>

          {/* Associates */}
          <TabsContent value="associates">
            <div className="rounded-2xl bg-white border border-border overflow-x-auto mt-4">
              <table className="w-full" data-testid="associates-table">
                <thead>
                  <tr>
                    <th className={th}>Name</th>
                    <th className={th}>Age</th>
                    <th className={th}>Phone</th>
                    <th className={th}>Area</th>
                    <th className={th}>Experience</th>
                    <th className={th}>Applied</th>
                  </tr>
                </thead>
                <tbody>
                  {associates.map((a) => (
                    <tr key={a.id} data-testid={`associate-row-${a.id}`}>
                      <td className={td}>{a.name}</td>
                      <td className={td}>{a.age}</td>
                      <td className={td}>{a.phone}</td>
                      <td className={td}>{a.city || "—"}</td>
                      <td className={`${td} max-w-[300px]`}>{a.experience || "—"}</td>
                      <td className={`${td} text-xs text-brand-ink/50 whitespace-nowrap`}>{fmt(a.created_at)}</td>
                    </tr>
                  ))}
                  {associates.length === 0 && (
                    <tr><td className={`${td} text-brand-ink/40`} colSpan={6}>No associate applications yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>

        <p className="text-xs text-brand-ink/40 mt-6">
          Auto-refreshes every 20 seconds. This is your working list of submitted client details.
        </p>
      </main>
    </div>
  );
}
